import { HarnessTranscriptEntry } from '@flowx/designer/modules/ai-builder/types/harness-session.types'
import { HarnessSessionEvent } from '@flowx/designer/modules/ai-builder/types/harness.types'
import { asString } from '@flowx/designer/modules/ai-builder/utils/payload.util'

/**
 * Giving a rendered turn the id the harness knows it by.
 *
 * The harness stamps `data.message_id` onto every event it persists and deliberately leaves the SSE
 * stream alone, so a turn that has only streamed has nothing to address. The ids arrive with the
 * next `GET /sessions/{id}`, and this is the fold that puts them on the transcript already on
 * screen — rather than rebuilding it through `restoreSession`, which would reset every entry's
 * clock to the moment of the reload.
 *
 * Pure, and matched on *rateable* entries rather than on position in the whole list: the transcript
 * carries lines the harness never saw (a cancelled turn's "Stopped. Nothing was saved.") and a
 * blind index would hand those somebody else's id.
 */

/** The entry types that stand for something the agent said, and so can carry an id: prose, a
 * proposal it put up, and an interrupt it stopped at. One for each rateable event below. */
export type RateableEntry = Extract<
  HarnessTranscriptEntry,
  { type: 'assistant' } | { type: 'artefact-marker' } | { type: 'interrupt' }
>

/**
 * The events that leave a rateable entry behind — one name per member of `RateableEntry`.
 *
 * An allowlist rather than a list of exclusions, because the two sides of the pairing have to name
 * the same set: an event counted here with no entry to consume it slides every later id onto the
 * wrong message. Listing what is *not* rateable would make a new harness event rateable by default
 * and desync silently; this way an unknown event costs its own turn its thumbs and nothing else.
 *
 * Narrower than what the harness accepts. It would also file a verdict against `planning_failed`,
 * `error` and `branch_ready`, but each becomes a notice here, and a notice carries no id — some are
 * written locally and were never a message the harness saw.
 */
export const RATEABLE_EVENTS: ReadonlySet<string> = new Set([
  // Prose, coalesced into one `assistant` entry per run.
  'token',
  // The three names every interrupt arrives under: a question, a proposal, a review.
  'ask_user',
  'plan_proposed',
  'interrupt',
])

export function isRateable(entry: HarnessTranscriptEntry): entry is RateableEntry {
  return (
    entry.type === 'assistant' || entry.type === 'artefact-marker' || entry.type === 'interrupt'
  )
}

/**
 * One entry per rateable event, in the order the harness kept them — blank where it recorded no
 * id, which a session that spans the deploy of the feedback routes will have.
 *
 * Deliberately not compacted: the blanks are what hold the positions, and dropping them would
 * slide every later id onto the wrong entry rather than leaving one entry unrateable.
 */
export function messageIdsFromEvents(events: readonly HarnessSessionEvent[]): string[] {
  return events
    .filter((event) => RATEABLE_EVENTS.has(event.event))
    .map((event) => asString(event.data['message_id']))
}

/** The transcript with ids stamped onto the entries that lacked them. Entries that already carry
 * one keep it, so a second read cannot renumber a conversation. */
export function withMessageIds(
  transcript: readonly HarnessTranscriptEntry[],
  events: readonly HarnessSessionEvent[]
): HarnessTranscriptEntry[] {
  const ids = messageIdsFromEvents(events)

  // Positions are paired off in order, counting every rateable entry — including ones already
  // stamped by an earlier read, which is what keeps a second turn's ids from sliding onto the
  // first turn's lines.
  const idByPosition = new Map(
    transcript
      .flatMap((entry, position) => (isRateable(entry) ? [position] : []))
      .map((position, rank) => [position, ids[rank]])
  )

  return transcript.map((entry, position) => {
    const id = idByPosition.get(position)

    return !id || !isRateable(entry) || entry.messageId ? entry : { ...entry, messageId: id }
  })
}
